/**
 * Elegir una o varias opciones de una lista larga para filtrar: las marcas en
 * la tabla de productos del backoffice (ADM-02), y categorias y marcas en los
 * resultados de busqueda.
 *
 * Mismo trato que `SelectorCategorias`: se monta solo mientras esta abierto y
 * no filtra nada hasta "Aplicar". Las elegidas van arriba de la lista, para
 * no tener que buscarlas de nuevo entre cientos de opciones al volver a abrir.
 */
import { useId, useState } from 'react';


import { numero } from '../../utilidades/formato';
import { contieneTexto } from '../../utilidades/texto-busqueda';
import { Modal } from '../administracion/Modal';

/** Una opcion con cuantos productos la tienen. */
export interface OpcionSelector {
  slug: string;
  nombre: string;
  cantidad: number;
}

export interface SelectorOpcionesProps {
  /** Titulo del modal, p. ej. "Filtrar por marca". */
  titulo: string;
  /** Que se elige, en singular y minuscula: "marca", "categoria". */
  que: string;
  opciones: OpcionSelector[];
  elegidas: string[];
  onAplicar(elegidas: string[]): void;
  onCerrar(): void;
}

export function SelectorOpciones({
  titulo,
  que,
  opciones,
  elegidas,
  onAplicar,
  onCerrar,
}: SelectorOpcionesProps) {
  const [marcadas, setMarcadas] = useState(() => new Set(elegidas));
  const [busqueda, setBusqueda] = useState('');
  const idBusqueda = useId();

  // El orden de "arriba las elegidas" se fija al abrir: si se reordenara al
  // marcar, la fila saltaria de lugar bajo el dedo.
  const [primero] = useState(() => new Set(elegidas));
  const ordenadas = [
    ...opciones.filter((opcion) => primero.has(opcion.slug)),
    ...opciones.filter((opcion) => !primero.has(opcion.slug)),
  ];
  const visibles = ordenadas.filter((opcion) => contieneTexto(opcion.nombre, busqueda));

  function marcar(slug: string, marcada: boolean): void {
    setMarcadas((actual) => {
      const siguiente = new Set(actual);
      if (marcada) siguiente.add(slug);
      else siguiente.delete(slug);
      return siguiente;
    });
  }

  return (
    <Modal titulo={titulo} bajada="Puedes elegir varias" abierto onCerrar={onCerrar}>
      <label className="solo-lectores" htmlFor={idBusqueda}>
        Buscar {que}
      </label>
      <input
        id={idBusqueda}
        type="search"
        className="entrada entrada-ancha"
        placeholder={`Buscar ${que}`}
        value={busqueda}
        onChange={(evento) => {
          setBusqueda(evento.target.value);
        }}
      />

      <ul className="lista-marcable" aria-label={titulo}>
        {visibles.length === 0 ? (
          <li className="lista-marcable-vacia">
            Ninguna {que} coincide con "{busqueda}".
          </li>
        ) : null}
        {visibles.map((opcion) => (
          <li key={opcion.slug}>
            <label className="fila-marcable">
              <input
                type="checkbox"
                checked={marcadas.has(opcion.slug)}
                onChange={(evento) => {
                  marcar(opcion.slug, evento.target.checked);
                }}
              />
              <span className="nombre">{opcion.nombre}</span>
              <span className="cuenta">{numero(opcion.cantidad)}</span>
            </label>
          </li>
        ))}
      </ul>

      <div className="modal-acciones separadas">
        <button
          type="button"
          className="btn btn-suave"
          disabled={marcadas.size === 0}
          onClick={() => {
            setMarcadas(new Set());
          }}
        >
          Desmarcar todo
        </button>
        <span className="acciones-fila">
          <button type="button" className="btn btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primario"
            onClick={() => {
              onAplicar(Array.from(marcadas));
            }}
          >
            Aplicar{marcadas.size > 0 ? ` (${marcadas.size})` : ''}
          </button>
        </span>
      </div>
    </Modal>
  );
}
